import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { formatHubBranchLabel } from '../../../utils/destinationType.js';
import { filterSelectClass, selectDropdownOpenClass } from '../../common/uiClasses.js';

const HubMultiSelect = ({
  hubs = [],
  value = [],
  onChange,
  placeholder = 'All Hubs',
  className = '',
  buttonClassName = '',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const selected = Array.isArray(value) ? value.map(String) : [];

  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (hubs || []).filter((hub) => {
      if (!q) return true;
      const label = `${formatHubBranchLabel(hub)} ${hub.location || ''} ${hub.hub_id || ''}`.toLowerCase();
      return label.includes(q);
    });
  }, [hubs, search]);

  useEffect(() => {
    const onClick = (event) => {
      const target = event.target;
      if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const trigger = buttonRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 4;
      const margin = 8;
      const width = Math.max(rect.width, 220);
      const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const openUp = spaceBelow < 240 && spaceAbove > spaceBelow;
      setMenuStyle({
        position: 'fixed',
        left,
        width,
        zIndex: 9999,
        ...(openUp
          ? { top: 'auto', bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap, bottom: 'auto' }),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const toggle = (hubId) => {
    const id = String(hubId);
    const next = selected.includes(id)
      ? selected.filter((item) => item !== id)
      : [...selected, id];
    onChange(next);
  };

  const label = selected.length
    ? `${selected.length} hub${selected.length === 1 ? '' : 's'} selected`
    : placeholder;

  return (
    <div ref={containerRef} className={`relative min-w-0 w-full md:w-auto ${className}`}>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className={`w-full min-w-0 ${compact ? 'sm:min-w-[140px] sm:max-w-[180px]' : 'sm:min-w-[220px]'} ${filterSelectClass} cursor-pointer flex justify-between items-center gap-2 ${buttonClassName} ${
          open ? selectDropdownOpenClass : ''
        }`}
      >
        <span className="min-w-0 truncate pr-2 font-medium text-gray-700">
          {label}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-700 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && menuStyle && typeof document !== 'undefined'
        ? createPortal(
        <div
          ref={panelRef}
          style={menuStyle}
          className="bg-white border border-gray-300 rounded-xl shadow-lg"
        >
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search hub..."
                autoFocus
                style={{ paddingLeft: '2.5rem' }}
                className="w-full py-1.5 pr-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full text-left px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 border-b border-gray-100"
              >
                All Hubs
              </button>
            )}
            {options.map((hub) => {
              const id = String(hub.id);
              return (
                <label key={id} className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={selected.includes(id)}
                    onChange={() => toggle(id)}
                    className="accent-blue-500"
                  />
                  <span className="truncate">{formatHubBranchLabel(hub) || hub.location || hub.hub_id}</span>
                </label>
              );
            })}
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No hubs found</div>
            )}
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
};

export default HubMultiSelect;
